import { Button } from '@mui/material'
import { appendSearchParamsToPathname, hydratePathname } from '../../../../util'
import { useNavigate } from 'react-router'
import { Route, RouteParameters, SearchParameters } from '../../../../constants'
import { useSearchParameters, useShop } from '../../../../hooks'
import { Reservation } from '../../../../types'
import { useTranslation } from 'react-i18next'

enum CreationMode {
  APPOINTMENT = 'appointment',
  RESERVATION = 'reservation',
}

const requiredParamsToCreateAppointment = [SearchParameters.CUSTOMER_GID, SearchParameters.JOURNEY, SearchParameters.OPPORTUNITY_GID]

interface Props {
  reservation: Reservation
}

const CreateNewButton = ({ reservation }: Props) => {
  const searchParameters = useSearchParameters()
  const { shop } = useShop()
  const { t } = useTranslation()
  const navigate = useNavigate()

  if (!reservation) {
    return null
  }

  // When this screen is called and we have opportunity information
  // Redirect the user to create an appointment instead of a reservation
  const hasAllRequiredParams = requiredParamsToCreateAppointment.every(
    (param) => searchParameters[param] !== undefined && searchParameters[param] !== null
  )
  const creationMode = hasAllRequiredParams ? CreationMode.APPOINTMENT : CreationMode.RESERVATION

  const onCreateNew = () => {
    const shopGid = searchParameters.shop_gid ?? shop?.gid
    const appointmentSearchParams = {
      [SearchParameters.CUSTOMER_GID]: searchParameters.customer_gid as string,
      [SearchParameters.JOURNEY]: searchParameters.journey as string,
      [SearchParameters.OPPORTUNITY_GID]: searchParameters.opportunity_gid as string,
      ...(shopGid ? { [SearchParameters.SHOP_GID]: shopGid } : {}),
    }
    const reservationSearchParams = {
      [SearchParameters.LEAD_GID]: reservation.lead_gid,
      [SearchParameters.TIMEZONE]: reservation.timezone,
      [SearchParameters.INSURANCE_COMPANY]: reservation.insurance_company ?? '',
      [SearchParameters.JOURNEY]: reservation.journey,
    }

    const route = creationMode === CreationMode.APPOINTMENT ? Route.CONSULTANT_SCHEDULE : Route.RESERVATION
    const searchParams = creationMode === CreationMode.APPOINTMENT ? appointmentSearchParams : reservationSearchParams

    const pathname = hydratePathname(route, {
      [RouteParameters.COUNTRY]: reservation.country,
    })
    const pathnameAndSearch = appendSearchParamsToPathname({
      pathname,
      searchParams,
    })

    navigate(pathnameAndSearch, { replace: true })
  }

  const createButtonText =
    creationMode === CreationMode.APPOINTMENT ? 'common.label.createNewAppointment' : 'common.label.createNewReservation'

  return (
    <Button onClick={onCreateNew} variant="contained">
      {t(createButtonText)}
    </Button>
  )
}

export { CreateNewButton }
